/**
 * Role Master API Service
 *
 * Centralised role database management with import/export,
 * position linkage, and audit trail.
 */

import { apiClient } from '../apiClient';

// =============================================================================
// TYPES
// =============================================================================

export type RoleMasterStatus = 'active' | 'deprecated' | 'draft';

export interface RoleMasterEntry {
  id: number;
  role_code: string;
  title: string;
  title_normalized: string;
  job_family?: string;
  functional_area?: string;
  grade_level?: string;
  department?: string;
  is_academic: boolean;
  position_level?: string;
  team_1?: string;
  team_2?: string;
  team_3?: string;
  status: RoleMasterStatus;
  source: string;
  fo_title_id?: string;
  fo_job_id?: string;
  sage_title_code?: string;
  remco_grade?: string;
  remchannel_match_code?: string;
  remchannel_grade_match?: string;
  remchannel_title_match?: string;
  finance_department_name?: string;
  finance_department_code?: string;
  role_allocation_type?: string;   // CE national / MC regional / C campus
  essential_campus_operations?: boolean;
  campus_manco_member?: boolean;
  remote_work?: string;
  customer_facing?: boolean;
  revenue_generating?: string;
  is_critical_role?: boolean;
  is_critical_cohort?: boolean;
  occupational_level?: string;
  role_cluster_skills?: string;
  role_cluster_grade_1?: string;
  role_cluster_grade_2?: string;
  budget_status?: string;
  notes?: string;
  effective_date?: string;
  expiration_date?: string;
  linked_positions_count: number;
  created_at: string;
  updated_at?: string;
}

export interface RoleMasterChangeLog {
  id: number;
  role_master_entry_id: number;
  change_type: string;
  field_changed?: string;
  previous_value?: string;
  new_value?: string;
  changed_by_user_id?: number;
  changed_by_name?: string;
  change_reason?: string;
  created_at: string;
}

export interface RoleMasterDetail extends RoleMasterEntry {
  change_logs: RoleMasterChangeLog[];
}

export interface RoleMasterListResponse {
  items: RoleMasterEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface RoleMasterCreateData {
  title: string;
  job_family?: string;
  functional_area?: string;
  grade_level?: string;
  department?: string;
  team_1?: string;
  team_2?: string;
  team_3?: string;
  is_academic?: boolean;
  position_level?: string;
  status?: RoleMasterStatus;
  fo_title_id?: string;
  fo_job_id?: string;
  sage_title_code?: string;
  remco_grade?: string;
  remchannel_match_code?: string;
  remchannel_grade_match?: string;
  remchannel_title_match?: string;
  finance_department_name?: string;
  finance_department_code?: string;
  role_allocation_type?: string;   // CE national / MC regional / C campus
  essential_campus_operations?: boolean;
  campus_manco_member?: boolean;
  remote_work?: string;
  customer_facing?: boolean;
  revenue_generating?: string;
  is_critical_role?: boolean;
  is_critical_cohort?: boolean;
  occupational_level?: string;
  role_cluster_skills?: string;
  role_cluster_grade_1?: string;
  role_cluster_grade_2?: string;
  budget_status?: string;
  notes?: string;
  effective_date?: string;
  expiration_date?: string;
}

// PUT /org/role-master/{id} is a PARTIAL update: the API only touches keys
// present in the body. `undefined` is dropped by JSON.stringify, so clearing
// a field must send an explicit `null` — hence every field accepts null.
export interface RoleMasterUpdateData {
  title?: string | null;
  job_family?: string | null;
  functional_area?: string | null;
  grade_level?: string | null;
  department?: string | null;
  team_1?: string | null;
  team_2?: string | null;
  team_3?: string | null;
  is_academic?: boolean | null;
  position_level?: string | null;
  status?: RoleMasterStatus | null;
  fo_title_id?: string | null;
  fo_job_id?: string | null;
  sage_title_code?: string | null;
  remco_grade?: string | null;
  remchannel_match_code?: string | null;
  remchannel_grade_match?: string | null;
  remchannel_title_match?: string | null;
  finance_department_name?: string | null;
  finance_department_code?: string | null;
  role_allocation_type?: string | null;   // CE national / MC regional / C campus
  essential_campus_operations?: boolean | null;
  campus_manco_member?: boolean | null;
  remote_work?: string | null;
  customer_facing?: boolean | null;
  revenue_generating?: string | null;
  is_critical_role?: boolean | null;
  is_critical_cohort?: boolean | null;
  occupational_level?: string | null;
  role_cluster_skills?: string | null;
  role_cluster_grade_1?: string | null;
  role_cluster_grade_2?: string | null;
  budget_status?: string | null;
  notes?: string | null;
  effective_date?: string | null;
  expiration_date?: string | null;
  change_reason?: string | null;
}

export interface ImportPreviewRow {
  row_number: number;
  role_code: string;
  title: string;
  job_family?: string;
  functional_area?: string;
  grade_level?: string;
  department?: string;
  is_academic: boolean;
  position_level?: string;
  team_1?: string;
  team_2?: string;
  team_3?: string;
  remco_grade?: string;
  remchannel_match_code?: string;
  remchannel_grade_match?: string;
  remchannel_title_match?: string;
  finance_department_name?: string;
  finance_department_code?: string;
  role_allocation_type?: string;   // CE national / MC regional / C campus
  essential_campus_operations?: boolean;
  campus_manco_member?: boolean;
  remote_work?: string;
  customer_facing?: boolean;
  revenue_generating?: string;
  is_critical_role?: boolean;
  is_critical_cohort?: boolean;
  occupational_level?: string;
  role_cluster_skills?: string;
  role_cluster_grade_1?: string;
  role_cluster_grade_2?: string;
  budget_status?: string;
  status: string;
  existing_id?: number;
  notes?: string;
}

export interface ImportPreviewResponse {
  upload_id: string;
  filename: string;
  total_rows: number;
  new_entries: number;
  duplicates: number;
  updates: number;
  errors: number;
  preview_rows: ImportPreviewRow[];
  rows?: ImportPreviewRow[];  // Full set — sent back on confirm (cross-replica safe)
  error_details: string[];
}

export interface ImportResult {
  total_processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  error_details: string[];
}

export interface PositionBrief {
  id: number;
  title: string;
  position_code: string;
  status: string;
  campus_code?: string;
  campus_name?: string;
  incumbent_name?: string;
}

export interface RoleMasterPositionsResponse {
  role_master_entry_id: number;
  role_code: string;
  title: string;
  positions: PositionBrief[];
  total: number;
}

export interface RoleMasterStats {
  total_entries: number;
  active_entries: number;
  deprecated_entries: number;
  draft_entries: number;
  linked_to_positions: number;
  linked_to_fo_jobs: number;
  unlinked_entries: number;
  by_job_family: Record<string, number>;
  by_functional_area: Record<string, number>;
  by_grade_level: Record<string, number>;
}

// Reconciliation types
export interface FoJobSuggestion {
  job_id: string;
  fo_title_id: string;
  description?: string;
  confidence: number;
  match_type: string;
  title_mismatch: boolean;
  job_family_id?: string;
  function_id?: string;
}

export type SageCodeSyncState = 'in_sync' | 'out_of_sync' | 'no_link' | 'unknown';

export interface ReconciliationEntry {
  id: number;
  role_code: string;
  title: string;
  functional_area?: string;
  department?: string;
  grade_level?: string;
  sage_title_code?: string;
  fo_job_id?: string;
  fo_title_id?: string;
  fo_compensation_level?: string;
  grade_mismatch?: boolean;
  /** RM has a grade but F&O JobCompensations is empty — distinct from mismatch. */
  grade_missing?: boolean;
  /** F&O has a comp level but RM.grade_level is empty — inverse of grade_missing. */
  grade_only_in_fo?: boolean;
  link_status: 'linked' | 'unlinked' | 'mismatch';
  suggestions: FoJobSuggestion[];
  linked_positions_count: number;
  /** Sage title code sync state vs F&O ACCSITitleCode (null when sage_title_code is blank). */
  sage_code_sync_status?: SageCodeSyncState | null;
}

export interface ReconciliationResponse {
  entries: ReconciliationEntry[];
  total: number;
  linked_count: number;
  unlinked_count: number;
  mismatch_count: number;
}

/** Response from POST /role-master/bulk-update-compensations. */
export interface BulkUpdateCompensationsResponse {
  total: number;
  updated: number;       // mismatch → DELETE+POST succeeded
  created: number;       // missing → POST succeeded
  skipped: number;       // F&O already had the same level
  errors: number;        // count of failed entries
  error_details: { entity: string; key: string; error: string }[];
}

/** One RM entry whose grade is out-of-sync with its linked F&O Job. */
export interface GradeSyncIssue {
  id: number;
  role_code: string;
  title: string;
  grade_level: string;
  fo_job_id: string;
  fo_compensation_level?: string | null;  // null = missing in F&O
  grade_mismatch: boolean;
  grade_missing: boolean;
}

/** Response from GET /role-master/grade-sync-issues. */
export interface GradeSyncIssuesResponse {
  total: number;
  mismatch_count: number;
  missing_count: number;
  issues: GradeSyncIssue[];
}

/** Response from POST /role-master/reconciliation/adopt-fo-grade. */
export interface AdoptFoGradeResponse {
  total: number;
  updated: number;
  skipped: { entry_id: number; reason: string }[];
}

export interface AutoLinkDetail {
  role_master_entry_id: number;
  rm_title: string;
  job_id: string;
  fo_title_id: string;
  confidence: number;
  title_mismatch: boolean;
}

export interface BulkAutoLinkResponse {
  linked: number;
  skipped: number;
  details: AutoLinkDetail[];
}

export interface DerivePositionsResponse {
  positions_linked: number;
  positions_already_linked: number;
  positions_no_match: number;
  positions_unlinked?: number;  // Stale links cleared on full rebuild
}

/** Someone holding an HR role, as a chair may pick them for an invite. */
export interface HrPerson {
  id: number;
  employee_id: string | null;
  display_name: string;
  email: string | null;
  job_title: string | null;
  campus: string | null;
}

/** A current Role Master title, as any signed-in user may read it. */
export interface RoleTitle {
  role_code: string;
  title: string;
  job_family?: string | null;
  fo_job_id?: string | null;
}

export interface RoleMasterBrief {
  id: number;
  role_code: string;
  title: string;
  fo_job_id?: string;
  fo_title_id?: string;
  /**
   * Job family — drives the F&O Position's JobFunctionId on
   * bulk-position-create (CreatePositionsModal pulls this through to
   * ``BulkPositionRowSpec.job_family_id``). Optional: legacy RM entries
   * may have this blank.
   */
  job_family?: string;
  linked_positions_count: number;
}

export interface ReassignPositionsRequest {
  target_entry_id: number;
  position_ids: number[];
  update_fo?: boolean;
}

export interface ReassignPositionDetail {
  position_id: number;
  position_code: string;
  title: string;
  db_updated: boolean;
  fo_updated?: boolean;
  fo_error?: string;
}

export interface ReassignPositionsResponse {
  total_requested: number;
  db_updated: number;
  fo_updated: number;
  fo_failed: number;
  fo_skipped: number;
  details: ReassignPositionDetail[];
}

export interface ImpactPreviewResponse {
  job_id: string;
  current_title: string;
  new_title: string;
  positions_affected: number;
  workers_affected: number;
  position_details: Array<{ position_id: string; current_title: string; worker_name: string }>;
  worker_details: Array<{ personnel_number: string; name: string }>;
}

export interface UpdateTitleResponse {
  job_updated: boolean;
  title_created: boolean;
  positions_updated: number;
  workers_updated: number;
  errors: Array<{ entity: string; key: string; success: boolean; error?: string }>;
  total_entities_affected: number;
}

export interface FoJob {
  job_id: string;
  title_id: string;
  description: string;
  job_family_id: string;
  function_id: string;
}

export interface FilterOptions {
  job_families: string[];
  functional_areas: string[];
  grade_levels: string[];
  position_levels: string[];
  departments: string[];
  teams_1: string[];
  teams_2: string[];
  teams_3: string[];
}

export interface RoleMasterListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: RoleMasterStatus;
  job_family?: string;
  functional_area?: string;
  department?: string;
  team_1?: string;
  team_2?: string;
  team_3?: string;
  grade_level?: string;
  is_academic?: boolean;
}

// =============================================================================
// UNIFIED VIEW (Roles + Reconciliation combined)
// =============================================================================

export interface UnifiedRoleMasterEntry extends RoleMasterEntry {
  link_status: 'linked' | 'unlinked' | 'mismatch';
  fo_compensation_level?: string;
  grade_mismatch: boolean;
  /** RM has a grade but F&O JobCompensations is empty — distinct from mismatch. */
  grade_missing?: boolean;
  /** F&O has a comp level but RM.grade_level is empty — inverse of grade_missing. */
  grade_only_in_fo?: boolean;
  suggestions: FoJobSuggestion[];
  /** Sage title code sync state vs F&O ACCSITitleCode (null when sage_title_code is blank). */
  sage_code_sync_status?: SageCodeSyncState | null;
}

// =============================================================================
// SAGE TITLE CODE SYNC (RM → F&O ACCSITitleCode)
// =============================================================================

export interface LinkedTitleStatus {
  title_id: string;
  source: 'rm_direct' | 'via_job';
  current_accsi_code?: string | null;
  in_sync: boolean;
}

export interface SageCodeSyncEntry {
  entry_id: number;
  role_code: string;
  title: string;
  rm_sage_code: string;
  status: SageCodeSyncState;
  linked_titles: LinkedTitleStatus[];
}

export interface SageCodeSyncStats {
  total_with_code: number;
  in_sync: number;
  out_of_sync: number;
  no_link: number;
  unknown: number;
}

export interface SageCodeSyncResponse {
  stats: SageCodeSyncStats;
  entries: SageCodeSyncEntry[];
  /** Populated when the F&O ACCSITitleCode batch fetch failed. When non-null,
   *  all entries have status "unknown" and the UI should surface this. */
  fetch_error?: string | null;
}

export interface PushSageCodeResult {
  entry_id: number;
  role_code: string;
  sage_code: string;
  target_title_ids: string[];
  updated: number;
  created: number;
  errors: string[];
}

export interface PushSageCodeResponse {
  total_entries: number;
  total_titles: number;
  updated: number;
  created: number;
  errors: number;
  details: PushSageCodeResult[];
}

export interface UnifiedStats {
  total_entries: number;
  active_entries: number;
  deprecated_entries: number;
  draft_entries: number;
  linked_count: number;
  unlinked_count: number;
  mismatch_count: number;
  /** Global count: RM has a grade, F&O has a *different* grade */
  grade_mismatch_count?: number;
  /** Global count: RM has a grade, F&O has no compensation record */
  grade_missing_count?: number;
  /** Global count: F&O has a comp level, RM.grade_level is empty */
  grade_only_in_fo_count?: number;
}

export interface UnifiedListResponse {
  items: UnifiedRoleMasterEntry[];
  total: number;
  page: number;
  page_size: number;
  stats: UnifiedStats;
}

export interface UnifiedListParams extends RoleMasterListParams {
  link_status?: 'linked' | 'unlinked' | 'mismatch';
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

const BASE = '/api/v1/identity/org/role-master';

const roleMasterService = {
  // CRUD
  async list(params: RoleMasterListParams = {}): Promise<RoleMasterListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    if (params.search) searchParams.set('search', params.search);
    if (params.status) searchParams.set('status', params.status);
    if (params.job_family) searchParams.set('job_family', params.job_family);
    if (params.functional_area) searchParams.set('functional_area', params.functional_area);
    if (params.department) searchParams.set('department', params.department);
    if (params.team_1) searchParams.set('team_1', params.team_1);
    if (params.team_2) searchParams.set('team_2', params.team_2);
    if (params.team_3) searchParams.set('team_3', params.team_3);
    if (params.grade_level) searchParams.set('grade_level', params.grade_level);
    if (params.is_academic !== undefined) searchParams.set('is_academic', String(params.is_academic));
    const query = searchParams.toString();
    const response = await apiClient.get<RoleMasterListResponse>(`${BASE}${query ? `?${query}` : ''}`);
    return response.data;
  },

  async listUnified(params: UnifiedListParams = {}): Promise<UnifiedListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    if (params.search) searchParams.set('search', params.search);
    if (params.status) searchParams.set('status', params.status);
    if (params.link_status) searchParams.set('link_status', params.link_status);
    if (params.functional_area) searchParams.set('functional_area', params.functional_area);
    if (params.department) searchParams.set('department', params.department);
    if (params.team_1) searchParams.set('team_1', params.team_1);
    if (params.team_2) searchParams.set('team_2', params.team_2);
    if (params.team_3) searchParams.set('team_3', params.team_3);
    if (params.grade_level) searchParams.set('grade_level', params.grade_level);
    const query = searchParams.toString();
    const response = await apiClient.get<UnifiedListResponse>(`${BASE}/unified${query ? `?${query}` : ''}`);
    return response.data;
  },

  async get(id: number): Promise<RoleMasterDetail> {
    const response = await apiClient.get<RoleMasterDetail>(`${BASE}/${id}`);
    return response.data;
  },

  async create(entry: RoleMasterCreateData): Promise<RoleMasterEntry> {
    const response = await apiClient.post<RoleMasterEntry>(BASE, entry);
    return response.data;
  },

  async update(id: number, entry: RoleMasterUpdateData): Promise<RoleMasterEntry> {
    const response = await apiClient.put<RoleMasterEntry>(`${BASE}/${id}`, entry);
    return response.data;
  },

  async deprecate(id: number, reason?: string): Promise<RoleMasterEntry> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await apiClient.delete<RoleMasterEntry>(`${BASE}/${id}${params}`);
    return response.data;
  },

  async reactivate(id: number, reason?: string): Promise<RoleMasterEntry> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await apiClient.post<RoleMasterEntry>(`${BASE}/${id}/reactivate${params}`);
    return response.data;
  },

  // People holding an HR role (hrbp, hr_admin, hr_manager, hr_specialist)
  async listUsersByRole(role: string): Promise<HrPerson[]> {
    const response = await apiClient.get<HrPerson[]>('/api/v1/identity/users/lookup/by-role', { params: { role } });
    return response.data;
  },

  // Current titles for pickers any team member may use (outside the Role Master gate)
  async listTitles(): Promise<RoleTitle[]> {
    const response = await apiClient.get<RoleTitle[]>('/api/v1/identity/org/role-titles');
    return response.data;
  },

  // Brief list for selectors
  async listBrief(status?: string): Promise<RoleMasterBrief[]> {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get<RoleMasterBrief[]>(`${BASE}/brief${params}`);
    return response.data;
  },

  // Positions linked to a role
  async getPositions(id: number): Promise<RoleMasterPositionsResponse> {
    const response = await apiClient.get<RoleMasterPositionsResponse>(`${BASE}/${id}/positions`);
    return response.data;
  },

  // Reassign positions to a different RM entry
  async reassignPositions(sourceId: number, data: ReassignPositionsRequest): Promise<ReassignPositionsResponse> {
    const response = await apiClient.post<ReassignPositionsResponse>(`${BASE}/${sourceId}/reassign-positions`, data);
    return response.data;
  },

  // Change log
  async getChangelog(id: number): Promise<RoleMasterChangeLog[]> {
    const response = await apiClient.get<RoleMasterChangeLog[]>(`${BASE}/${id}/changelog`);
    return response.data;
  },

  // Stats
  async getStats(): Promise<RoleMasterStats> {
    const response = await apiClient.get<RoleMasterStats>(`${BASE}/stats`);
    return response.data;
  },

  // Filter options
  async getFilterOptions(): Promise<FilterOptions> {
    const response = await apiClient.get<FilterOptions>(`${BASE}/filters/options`);
    return response.data;
  },

  // Import
  async previewImport(file: File): Promise<ImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ImportPreviewResponse>(`${BASE}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async confirmImport(
    preview: ImportPreviewResponse,
    skipDuplicates: boolean = true,
    updateExisting: boolean = false,
    createFoJobs: boolean = false,
  ): Promise<ImportResult> {
    const response = await apiClient.post<ImportResult>(`${BASE}/import/confirm`, {
      upload_id: preview.upload_id,
      skip_duplicates: skipDuplicates,
      update_existing: updateExisting,
      create_fo_jobs: createFoJobs,
      filename: preview.filename,
      rows: preview.rows ?? preview.preview_rows,
    });
    return response.data;
  },

  // Export
  async exportExcel(status?: RoleMasterStatus): Promise<Blob> {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get<Blob>(`${BASE}/export/excel${params}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Reconciliation
  async getReconciliation(linkStatus?: string): Promise<ReconciliationResponse> {
    const params = linkStatus ? `?link_status=${linkStatus}` : '';
    const response = await apiClient.get<ReconciliationResponse>(`${BASE}/reconciliation${params}`);
    return response.data;
  },

  async linkJob(entryId: number, jobId: string, foTitleId: string, updateFoTitle: boolean = false) {
    const response = await apiClient.post(`${BASE}/reconciliation/link`, {
      role_master_entry_id: entryId,
      job_id: jobId,
      fo_title_id: foTitleId,
      update_fo_title: updateFoTitle,
    });
    return response.data;
  },

  async unlinkJob(entryId: number) {
    const response = await apiClient.post(`${BASE}/reconciliation/unlink`, {
      role_master_entry_id: entryId,
    });
    return response.data;
  },

  async autoLink(minConfidence: number = 0.85, dryRun: boolean = true): Promise<BulkAutoLinkResponse> {
    const response = await apiClient.post<BulkAutoLinkResponse>(`${BASE}/reconciliation/auto-link`, {
      min_confidence: minConfidence,
      dry_run: dryRun,
    });
    return response.data;
  },

  async updateFoTitle(entryId: number): Promise<UpdateTitleResponse> {
    const response = await apiClient.post<UpdateTitleResponse>(`${BASE}/reconciliation/update-fo-title`, {
      role_master_entry_id: entryId,
    });
    return response.data;
  },

  async derivePositions(): Promise<DerivePositionsResponse> {
    const response = await apiClient.post<DerivePositionsResponse>(`${BASE}/reconciliation/derive-positions`);
    return response.data;
  },

  /**
   * Bulk-push RM grade_level → F&O JobCompensations.CompensationLevelId.
   *
   * Fixes two scenarios in one call:
   *  - **Mismatch**: F&O has a different grade. The endpoint DELETEs the old
   *    JobCompensations row and POSTs a new one (the entity is keyed on
   *    JobId+CompensationLevelId, so the level field cannot be PATCHed).
   *  - **Missing**: F&O has no compensation record at all. Single POST.
   *
   * Pass `existing_level` from whatever the UI currently shows; the backend
   * re-verifies against a fresh F&O snapshot before deciding the action.
   */
  async bulkUpdateCompensations(
    entries: { job_id: string; compensation_level: string; existing_level?: string | null }[]
  ): Promise<BulkUpdateCompensationsResponse> {
    const response = await apiClient.post<BulkUpdateCompensationsResponse>(
      `${BASE}/bulk-update-compensations`,
      { entries },
    );
    return response.data;
  },

  /**
   * Pull the F&O compensation level into RM.grade_level for the given linked
   * entries (the "grade only set in F&O" case). RM adopts F&O as the source of
   * truth — the inverse of bulkUpdateCompensations.
   */
  async adoptFoGrade(entryIds: number[]): Promise<AdoptFoGradeResponse> {
    const response = await apiClient.post<AdoptFoGradeResponse>(
      `${BASE}/reconciliation/adopt-fo-grade`,
      { entry_ids: entryIds },
    );
    return response.data;
  },

  /**
   * Return every active RM entry whose grade is out-of-sync with F&O —
   * either F&O has a different grade (mismatch) or F&O has no compensation
   * record at all (missing). Used to drive the "Push N grades to F&O"
   * bulk action across the whole RM, not just the current table page.
   */
  async getGradeSyncIssues(): Promise<GradeSyncIssuesResponse> {
    const response = await apiClient.get<GradeSyncIssuesResponse>(`${BASE}/grade-sync-issues`);
    return response.data;
  },

  async searchFoJobs(query: string): Promise<{ jobs: FoJob[]; total: number }> {
    const response = await apiClient.get<{ jobs: FoJob[]; total: number }>(`${BASE}/reconciliation/search-fo-jobs`, {
      params: { q: query },
    });
    return response.data;
  },

  async seedOrgUnits(dryRun: boolean = true): Promise<{
    dry_run: boolean;
    functions_created: number;
    functions_total: number;
    departments_created: number;
    departments_total: number;
    teams_created: number;
    team_details: Array<{ dept: string; team_1: string; team_2?: string; team_3?: string; level: number }>;
  }> {
    const response = await apiClient.post<any>(`${BASE}/seed-org-units`, null, {
      params: { dry_run: dryRun },
    });
    return response.data;
  },

  async deriveSageCodes(dryRun: boolean = true) {
    const response = await apiClient.post(`${BASE}/derive-sage-codes`, null, {
      params: { dry_run: dryRun },
    });
    return response.data as {
      preview: Array<{ id: number; role_code: string; title: string; derived_sage_code: string }>;
      total: number;
      applied: number;
      existing_codes_count: number;
      dry_run: boolean;
    };
  },

  async pushSageCodesToFo(dryRun: boolean = true): Promise<PushSageCodeResponse> {
    const response = await apiClient.post<PushSageCodeResponse>(
      `${BASE}/push-sage-codes-to-fo`,
      null,
      { params: { dry_run: dryRun } },
    );
    return response.data;
  },

  // ── Sage title code sync (RM → F&O ACCSITitleCode) ────────────────

  /** Fetch live sync status: diffs RM.sage_title_code vs F&O ACCSITitleCode. */
  async getSageCodeSyncStatus(onlyOutOfSync = false): Promise<SageCodeSyncResponse> {
    const response = await apiClient.get<SageCodeSyncResponse>(
      `${BASE}/sage-code-sync-status`,
      { params: { only_out_of_sync: onlyOutOfSync } },
    );
    return response.data;
  },

  /** Force-sync one RM entry's sage code to every linked F&O Title. */
  async pushSageCodeForEntry(entryId: number): Promise<PushSageCodeResponse> {
    const response = await apiClient.post<PushSageCodeResponse>(
      `${BASE}/${entryId}/push-sage-code`,
    );
    return response.data;
  },

  /** Force-sync multiple RM entries in a single call. */
  async pushSageCodesForEntries(entryIds: number[]): Promise<PushSageCodeResponse> {
    const response = await apiClient.post<PushSageCodeResponse>(
      `${BASE}/push-sage-codes-batch`,
      { entry_ids: entryIds },
    );
    return response.data;
  },
};

export default roleMasterService;
